import React, {useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {DraftProjectStatus, ProjectType} from '@contentlab/api/project/types';
import FeatureEditor from '@contentlab/components/feature-editor';
import ChooseResolutions from '@contentlab/components/feature-editor/components/choose-resolutions/ChooseResolutions';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import {
  DEFAULT_DEBOUNCE_DELAY,
  PROJECT_ID_SEARCH_PARAM_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {Resolution} from '@contentlab/types/resolution';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets/useFeatureAssets';
import {useDebouncedCallback} from '@/hooks/use-debounce';

function UpscalePage() {
  const {draftUpscaleProject} = contentLabStore(
    useShallow((state) => ({
      draftUpscaleProject: state.draftUpscaleProject,
    }))
  );

  const {uploadedVideos, assetErrors: clientAssetErrors} = useFeatureAssets(
    FeatureName.UPSCALE
  );

  const serverAssetErrors =
    draftUpscaleProject?.validationErrors?.userInput?.assets || [];

  const {
    assetErrors,
    invalidAssetIds,
    validUploadedVideos,
    validClientUploadedVideos,
  } = useMergedAssetErrors({
    serverAssetErrors,
    clientAssetErrors,
    uploadedVideos,
  });

  const {uiInteraction} = useUiInteractionEvent();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: draftProjectData,
    createDraftProject,
    updateDraftProject,
    resetDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.UPSCALE,
    draftProject: draftUpscaleProject,
    projectId: searchParams.get(PROJECT_ID_SEARCH_PARAM_KEY),
    onGetProjectErrorCallback: () => {
      searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
      setSearchParams(searchParams);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const getProjectName = () =>
    contentLabStore.getState().draftUpscaleProject.projectName;

  const getUpscaleSpecs = (assets?: Asset[], excludeInvalidAssets = false) => {
    const resolutions =
      contentLabStore.getState().draftUpscaleProject.selectedResolutions;

    const assetsSource = excludeInvalidAssets
      ? (assets || readyAssets).filter(
          (asset) => !invalidAssetIds.has(asset.id)
        )
      : assets || readyAssets;

    const specs = assetsSource.flatMap((asset) => {
      const minDimension = Math.min(
        asset.metadata?.width || 0,
        asset.metadata?.height || 0
      );
      const validResolutions = resolutions.filter(
        (resolution) => minDimension < resolution
      );

      return validResolutions.map((resolution) => ({
        assetId: asset.id,
        outputResolution: resolution,
      }));
    });

    return specs;
  };

  const createProject = async (assets?: Asset[]) => {
    if (draftUpscaleProject.isInitialized) {
      return;
    }

    try {
      uiInteraction({
        item_name: 'create_upscale_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
        flow_name: FLOW_NAMES.CONTENT_LAB.UPSCALE,
        interaction_id: uuid(),
      });

      await createDraftProject({
        type: ProjectType.UPSCALE,
        projectName: getProjectName(),
        draft: {
          upscaleSpecs: getUpscaleSpecs(assets),
        },
      });
    } catch (error) {
      // No operation
    }
  };
  const updateProject = async (
    status: DraftProjectStatus = DraftProjectStatus.UPDATE_DRAFT,
    assets?: Asset[]
  ) => {
    if (!draftUpscaleProject.isInitialized) {
      return;
    }

    uiInteraction({
      item_name: 'update_upscale_project',
      interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
      flow_name: FLOW_NAMES.CONTENT_LAB.UPSCALE,
      interaction_id: uuid(),
    });

    await updateDraftProject({
      status,
      projectName: getProjectName(),
      draft: {
        upscaleSpecs: getUpscaleSpecs(
          assets,
          status === DraftProjectStatus.EXECUTE
        ),
      },
    });
  };

  const updateOrCreateProject = async (assets?: Asset[]) => {
    if (!draftUpscaleProject.isInitialized) {
      await createProject(assets);
    } else {
      await updateProject(DraftProjectStatus.UPDATE_DRAFT, assets);
    }
  };

  const debouncedUpdateOrCreateProject = useDebouncedCallback(
    updateOrCreateProject,
    DEFAULT_DEBOUNCE_DELAY
  );

  const readyAssets = useReadyAssets({
    assetIds: validClientUploadedVideos.map(
      (uploadedVideo) => uploadedVideo.id
    ),
    onReady: (assets) => updateOrCreateProject(assets),
    keepPreviousData: false,
  });

  const getDisabledResolutions = (): Resolution[] => {
    if (uploadedVideos.length === 0) return [];

    // Get the minimum dimension (height or width) across all uploaded videos
    const minDimension = Math.min(
      ...uploadedVideos.map((video) =>
        Math.min(video.height || 0, video.width || 0)
      )
    );

    // Return all resolutions that are lower than this minimum dimension
    return Object.values(Resolution).filter(
      (resolution) =>
        typeof resolution === 'number' && resolution <= minDimension
    ) as Resolution[];
  };

  // Calculate required credits by filtering out identity transformations
  const getRequiredCredits = () =>
    validUploadedVideos.reduce((assetTokens, asset) => {
      const validTransformations =
        draftUpscaleProject.selectedResolutions.filter(
          (targetResolution: Resolution) =>
            Math.min(asset.height || 0, asset.width || 0) < targetResolution
        );
      const assetTokenCount = calculateVideoTokensForAssets([asset]);
      return assetTokens + assetTokenCount * validTransformations.length;
    }, 0);

  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftUpscaleProject,
    defaultProjectName: 'Upscale',
    updateProject,
    resetProject: resetDraftProject,
  });
  const handleGenerateClick = async () => {
    setIsSubmitting(true);
    try {
      uiInteraction({
        item_name: 'execute_upscale_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
        flow_name: FLOW_NAMES.CONTENT_LAB.UPSCALE,
        interaction_id: uuid(),
      });
      await generateProject();
    } catch (error) {
      // No operation
    }
    setIsSubmitting(false);
  };

  const isGenerateButtonDisabled = useMemo(
    () =>
      uploadedVideos.length === 0 ||
      draftUpscaleProject.selectedResolutions.length === 0 ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isSubmitting ||
      !draftUpscaleProject.projectName ||
      getUpscaleSpecs(readyAssets, true).length === 0 ||
      isManagingProjectLoading ||
      readyAssets.length !== validClientUploadedVideos.length,
    [
      uploadedVideos,
      validClientUploadedVideos,
      draftUpscaleProject.selectedResolutions,
      isSubmitting,
      draftUpscaleProject.projectName,
      isManagingProjectLoading,
      readyAssets,
    ]
  );

  const isGenerateButtonLoading = useMemo(
    () =>
      isSubmitting ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isManagingProjectLoading,
    [isSubmitting, uploadedVideos, isManagingProjectLoading]
  );

  const disabledResolutions = getDisabledResolutions();

  const activeResolutions = useMemo(
    () =>
      draftUpscaleProject.selectedResolutions.filter(
        (resolution) => !disabledResolutions.includes(resolution)
      ),
    [draftUpscaleProject.selectedResolutions, disabledResolutions]
  );

  const generateProjectDescription = useMemo(() => {
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!isUploading && uploadedVideos.length === 0) {
      return translate('Upload videos and choose resolutions to generate');
    }
    if (uploadedVideos.length > 0 && !activeResolutions.length) {
      return translate('Choose resolutions to generate');
    }
    return '';
  }, [uploadedVideos, isSubmitting, activeResolutions]);

  return (
    <FeatureEditor
      feature={FeatureName.UPSCALE}
      featureDetailsProps={{
        featureDetailsComponent: (
          <ChooseResolutions
            disabledResolutions={disabledResolutions}
            selected={draftUpscaleProject.selectedResolutions}
            onSelectionChange={(resolutions) => {
              uiInteraction({
                item_name: 'choose_resolution',
                interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
                flow_name: FLOW_NAMES.CONTENT_LAB.UPSCALE,
                interaction_details: JSON.stringify({
                  resolution: resolutions.join(','),
                }),
              });
              contentLabActions.updateDraftUpscaleProjectResolutions(
                resolutions
              );
              debouncedUpdateOrCreateProject();
            }}
            tooltip={
              uploadedVideos.length === 1
                ? translate('File already meets or exceeds this resolution')
                : translate('Files already meet or exceed this resolution')
            }
          />
        ),
      }}
      renameProjectProps={{
        projectName: draftUpscaleProject.projectName,
        onBlur: (name) => {
          updateOrCreateProject();
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
            flow_name: FLOW_NAMES.CONTENT_LAB.UPSCALE,
            interaction_details: JSON.stringify({
              project_name: name,
            }),
          });
        },
        projectNameChange: (name) => {
          contentLabActions.updateDraftUpscaleProjectName(name);
        },
        projectNamePlaceholder: translate('Name your Upscale project'),
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.UPSCALE,
          validUploadedVideos.length,
          activeResolutions.length
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload videos'),
        guideTitle: translate('max_duration_and_size_per_file', {
          maxDuration: translate(
            getUploadLimits(FeatureName.UPSCALE).maxDurationDisplayKey
          ),
          maxSize: translate(
            getUploadLimits(FeatureName.UPSCALE).maxFileSizeDisplayKey
          ),
        }),
        guideDescription: translate(
          'Keep key elements centered, clean backgrounds, and stable footage.'
        ),
        shortenGuideText: translate(
          'Keep key elements centered, clean backgrounds, and stable footage.'
        ),
        readyAssets,
        assetErrors,
        onLearnMoreClick: () => {
          contentLabActions.setIsLearnMoreModalOpen(true);
          uiInteraction({
            item_name: 'learn more',
            interaction_location: CONTENT_LAB_VIEW_NAMES.UPSCALE,
          });
        },
      }}
    />
  );
}

export default UpscalePage;
